import React, { FC, createContext, useCallback, useEffect, useRef, useState } from 'react';

import { InvoiceTabEnum } from 'containers/invoices/create-invoice/constants';
import { GenericError, GenericModal, Loader } from 'components';
import { useCreateInvoiceMutation, useLazyGetInvoicePDFPreviewQuery } from 'containers/invoices/api';
import DateUtils from 'utils/dateUtils';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useNavigate } from 'react-router-dom';
import {
  selectCurrentTeam,
  selectInvoiceLimit,
  selectMerchantCustomizations,
  showNotifier
} from 'containers/app/appSlice';
import {
  checkIfAnyPaymentModeIsEligible,
  checkIfInvoiceTotalExceedsPaymentModeLimit,
  doesServiceChargeNameContainTax,
  getInvoiceThresholdValidationErrors,
  hasChangesToSync
} from 'containers/invoices/utils';
import {
  CreateInvoiceWithRiskValidationResponse,
  InvoiceStatus,
  TaxesAndFeesResponse
} from 'containers/invoices/invoices.model';
import InvoiceRiskWarningsPopup from '../components/InvoiceRiskWarningsPopup';
import { ErrorResponse } from 'containers/app/app.model';
import { APIErrorMsg, APIErrorStatuses } from 'services/type';
import { NotifierTypes } from 'constants/notifierConstants';
import { throttle } from 'utils/commonUtils';
import { logAnalyticEvent } from 'utils/analytics';
import { RiskControlActionType, webInvoiceCreateRiskControl } from '../events';
import { InvoicesRouteState } from 'routes/types';
import routesPath from 'routes/RoutesPath';
import { invoiceMinimumAmount } from 'constants/common';
import BillToSection from '../components/BillToSection';
import InvoiceLineItems from '../components/InvoiceLineItems';
import NotesSection from '../components/NotesSection';
import TermsSection from '../components/TermsSection';
import AttachmentsSection from '../components/AttachmentsSection';
import InvoiceTotalSection from '../components/InvoiceTotalSection';
import { StatusIndicator } from '../constants';
import TeamDetailsSection from '../components/TeamDetailsSection';
import InvoiceHeader from '../components/InvoiceHeader';
import {
  resetInvoiceState,
  selectAppliedLineItemLevelTaxes,
  selectDiscountFormData,
  selectDiscountSettings,
  selectDueDate,
  selectInvoice,
  selectInvoiceAttachments,
  selectInvoiceCustomer,
  selectInvoiceDate,
  selectInvoiceFinalAmount,
  selectInvoiceFromJobNumber,
  selectInvoiceLineItems,
  selectInvoiceNotes,
  selectInvoicePaymentModes,
  selectInvoiceSubtotal,
  selectIsInvoiceFromJob,
  selectIsServiceFeeEnabled,
  selectPartialPaymentEnabled,
  selectSelectedServiceCharge,
  updateInvoiceCreatedResponse
} from '../InvoicesSlice';
import { GetTeamProfileApiResponse } from 'containers/team-profile/teamProfile.model';
import { InvoiceErrorFieldsDefault } from '../create-invoice/types';
import { selectCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { CrossIconvector } from 'assets/icons';
import InvoiceErrorsPopup from '../components/InvoiceErrorsPopup';
import InvoiceTaxEncouragedPopup from '../components/InvoiceTaxEncouragedPopup';
import NoSelfCheckoutPopup from '../components/NoSelfCheckoutPopup';
import InvoiceLimitExceededPopup from '../components/InvoiceLimitExceededPopup';
import PartialPaymentAvailablePopup from '../components/PartialPaymentAvailablePopup';
import { getCreateInvoiceV2Payload, getPDFPreviewPayload } from '../create-invoice/util';
import { RelatedEntityData } from '../create-invoice/types';

interface ICreateInvoiceContext {
  selectedInvoiceTab: InvoiceTabEnum;
  setSelectedInvoiceTab: React.Dispatch<React.SetStateAction<InvoiceTabEnum>>;
}

export const CreateInvoiceContext = createContext<ICreateInvoiceContext>({
  selectedInvoiceTab: InvoiceTabEnum.BILL_TO,
  setSelectedInvoiceTab: null
});

enum PageState {
  BUYER_VIEW = 'BUYER_VIEW',
  PDF_VIEW = 'PDF_VIEW'
}

interface BuyersPreviewProps {
  goBack: () => void;
  getTeamProfileData: GetTeamProfileApiResponse;
  taxesAndFees: TaxesAndFeesResponse;
}

const BuyersPreview: FC<BuyersPreviewProps> = ({ goBack, getTeamProfileData, taxesAndFees }) => {
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>(PageState.BUYER_VIEW);

  const [selectedInvoiceTab, setSelectedInvoiceTab] = useState(InvoiceTabEnum.BILL_TO);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [getInvoicePDFPreview, getInvoicePDFPreviewQueryState] = useLazyGetInvoicePDFPreviewQuery();
  const {
    data: getInvoicePDFPreviewResponse,
    isSuccess: getPreviewSuccess,
    isFetching: isPDFPreviewFetching,
    isError: isPDFPreviewError
  } = getInvoicePDFPreviewQueryState;

  const selectedServiceCharge = useAppSelector(selectSelectedServiceCharge);
  const selectedCustomerForInvoice = useAppSelector(selectInvoiceCustomer);
  const selectedLineItemsForInvoice = useAppSelector(selectInvoiceLineItems);
  const selectDiscountSettingsForInvoice = useAppSelector(selectDiscountSettings);
  const selectDiscountFormDataForInvoice = useAppSelector(selectDiscountFormData);
  const selectInvoiceAttachmentsForInvoice = useAppSelector(selectInvoiceAttachments);
  const selectInvoiceNotesForInvoice = useAppSelector(selectInvoiceNotes);
  const isServiceFeeEnabled = useAppSelector(selectIsServiceFeeEnabled);
  const selectedAppliedLineItemLevelTaxes = useAppSelector(selectAppliedLineItemLevelTaxes);
  const partialPaymentEnabled = useAppSelector(selectPartialPaymentEnabled);
  const invoicePaymentModes = useAppSelector(selectInvoicePaymentModes);
  const invoiceLimits = useAppSelector(selectInvoiceLimit);
  const invoiceNumber = 'IN-XXXXX';

  const selectInvoiceSubtotalForInvoice = useAppSelector(selectInvoiceSubtotal);
  const selectInvoiceFinalAmountForInvoice = useAppSelector(selectInvoiceFinalAmount);
  const selectedInvoice = useAppSelector(selectInvoice);
  const customizations = useAppSelector(selectCustomizations);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInvoiceFromJob = useAppSelector(selectIsInvoiceFromJob);
  const invoiceFromJobNumber = useAppSelector(selectInvoiceFromJobNumber);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const invoiceDate = useAppSelector(selectInvoiceDate);
  const dueDate = useAppSelector(selectDueDate);

  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showNoSelfCheckoutPopup, setShowNoSelfCheckoutPopup] = useState(false);
  const [showInvoiceErrorsPopup, setShowInvoiceErrorsPopup] = useState({
    open: false,
    errors: InvoiceErrorFieldsDefault
  });
  const [showPartialPaymentAvailablePopup, setShowPartialPaymentAvailablePopup] = useState(false);
  const [showInvoiceLimitExceededPopup, setShowInvoiceLimitExceededPopup] = useState(false);
  const [showTaxEncouragedPopup, setShowTaxEncouragedPopup] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);

  const jobId = queryParams.get('jobId');

  const dispatch = useAppDispatch();

  const [
    createInvoiceMutation,
    {
      isSuccess: isCreateInvoiceSuccess,
      data: invoiceCreatedResponse,
      isError: isCreateInvoiceError,
      error: createInvoiceError,
      isLoading: isCreateInvoiceLoading
    }
  ] = useCreateInvoiceMutation();

  const [showRiskWarningsPopup, setShowRiskWarningsPopup] = useState(false);
  const [riskWarnings, setRiskWarnings] = useState<string[]>([]);
  const pendingSkipWarningsRef = useRef(false);
  const invoiceCreatedSuccessRef = useRef(false);
  const lastShownValidationErrorsRef = useRef<typeof InvoiceErrorFieldsDefault | null>(null);

  useEffect(() => {
    return () => {
      if (!invoiceCreatedSuccessRef.current) {
        logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.abandoned });
      }
    };
  }, []);

  useEffect(() => {
    if (!isCreateInvoiceSuccess || !invoiceCreatedResponse) {
      return;
    }
    const data = invoiceCreatedResponse as CreateInvoiceWithRiskValidationResponse;
    const hasRiskErrors = data.isRiskValidationFailed && (data.riskValidations?.errors?.length ?? 0) > 0;
    const hasWarnings = (data.riskValidations?.warnings?.length ?? 0) > 0;

    if (pendingSkipWarningsRef.current) {
      pendingSkipWarningsRef.current = false;
      setShowRiskWarningsPopup(false);
      setRiskWarnings([]);
      dispatch(resetInvoiceState());
      dispatch(updateInvoiceCreatedResponse(data));
      invoiceCreatedSuccessRef.current = true;
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.success });
      const invoiceRouteState: InvoicesRouteState = {
        showShareModal: true,
        isEditInvoiceShare: false,
        showSidePanel: false
      };
      navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
      return;
    }

    if (hasRiskErrors) {
      goBack();
      navigate(routesPath.INVOICES_CREATE, {
        replace: true,
        state: {
          riskValidationErrors: data.riskValidations.errors,
          riskWarnings: data.riskValidations.warnings ?? []
        }
      });
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_risk_errors_blocked,
        BackendErrorEntities: data.riskValidations.errors.map(e => e.entity).join(',')
      });
      return;
    }

    if (hasWarnings) {
      setShowRiskWarningsPopup(true);
      setRiskWarnings(data.riskValidations.warnings);
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_warning_shown,
        BackendWarningCount: data.riskValidations.warnings.length
      });
      return;
    }

    setShowRiskWarningsPopup(false);
    setRiskWarnings([]);
    dispatch(resetInvoiceState());
    dispatch(updateInvoiceCreatedResponse(data));
    invoiceCreatedSuccessRef.current = true;
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.success });
    const invoiceRouteState: InvoicesRouteState = {
      showShareModal: true,
      isEditInvoiceShare: false,
      showSidePanel: false
    };
    navigate(routesPath.INVOICES, { replace: true, state: invoiceRouteState });
  }, [isCreateInvoiceSuccess, invoiceCreatedResponse]);

  useEffect(() => {
    if (isCreateInvoiceError) {
      const error = createInvoiceError as ErrorResponse;
      const backendErrorDetail =
        typeof error?.data?.details === 'string'
          ? error.data.details
          : (error?.data as { message?: string })?.message ?? undefined;
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.backend_error,
        ...(backendErrorDetail && { BackendErrorDetail: backendErrorDetail })
      });
      if (error.status === APIErrorStatuses.STATUS_422) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: error.data.message,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else if (error.status === APIErrorStatuses.STATUS_429) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: APIErrorMsg.LIMIT_EXCEEDED,
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      } else {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Failed to create invoice',
              secondaryMessage: '',
              isMessageHtml: true
            },
            type: NotifierTypes.ERROR,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
      }
      navigate(routesPath.INVOICES_CREATE, { replace: true });
    }
  }, [isCreateInvoiceError, dispatch, navigate]);

  const handleCreateInvoiceFromJob = () => {
    const entityData: RelatedEntityData = {
      entityId: jobId,
      entityType: 'JOB',
      entityNumber: invoiceFromJobNumber
    };

    handleCreateInvoiceV8(entityData);
  };

  const handleCreateInvoiceV8 = (entityData?: RelatedEntityData, skipWarnings?: boolean) => {
    const v2Payload = getCreateInvoiceV2Payload(
      selectedInvoice,
      currentTeam,
      taxesAndFees.tnc,
      merchantCustomisations,
      entityData
    );
    const payload = skipWarnings ? { ...v2Payload, skipWarnings: true } : v2Payload;
    if (skipWarnings) {
      pendingSkipWarningsRef.current = true;
    }
    createInvoiceMutation({ teamId: currentTeam.id.toString(), payload });
  };

  const handleRiskWarningsCreateAnyway = useCallback(() => {
    setShowRiskWarningsPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_confirmed });
    if (isInvoiceFromJob) {
      const entityData: RelatedEntityData = {
        entityId: jobId,
        entityType: 'JOB',
        entityNumber: invoiceFromJobNumber
      };
      handleCreateInvoiceV8(entityData, true);
    } else {
      handleCreateInvoiceV8(undefined, true);
    }
  }, [isInvoiceFromJob, jobId, invoiceFromJobNumber]);

  const handleRiskWarningsGotIt = useCallback(() => {
    setShowRiskWarningsPopup(false);
    setRiskWarnings([]);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.backend_warning_closed });
  }, []);

  const onCreateAndShare = () => {
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.attempted });
    let isPageIncomplete = false;
    let errors = InvoiceErrorFieldsDefault;
    if (selectedInvoice.invoiceLineItems.length === 0) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    if (selectedInvoice.invoiceCustomer === null) {
      isPageIncomplete = true;
      errors = { ...errors, customer: true };
    }
    if (+selectedInvoice.invoiceSubTotal < (+merchantCustomisations?.minimumAmount || +invoiceMinimumAmount)) {
      isPageIncomplete = true;
      errors = { ...errors, totalAmount: true };
    }
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      const isAnyPaymentModeEligible = checkIfAnyPaymentModeIsEligible(
        customizations.paymentModes,
        selectedInvoice.finalAmount,
        partialPaymentEnabled
      );
      if (!isAnyPaymentModeEligible) {
        isPageIncomplete = true;
        errors = { ...errors, paymentMethods: true };
      }
    }

    const partialPaymentRiskLimit = merchantCustomisations?.invoiceLimits?.partialPaymentRiskThresholdLimit ?? '';
    const usePartialPaymentRiskLimit =
      partialPaymentEnabled && partialPaymentRiskLimit !== '' && +partialPaymentRiskLimit > 0;
    const effectiveThreshold = usePartialPaymentRiskLimit
      ? partialPaymentRiskLimit
      : merchantCustomisations?.invoiceLimits?.invoiceValidationThreshold || '';

    const thresholdErrors = getInvoiceThresholdValidationErrors({
      totalAmount: selectedInvoice.finalAmount,
      threshold: effectiveThreshold,
      customer: selectedInvoice.invoiceCustomer
    });
    if (Object.keys(thresholdErrors).length > 0) {
      isPageIncomplete = true;
      errors = { ...errors, ...thresholdErrors };
    }

    const taxMandatoryThreshold = +merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold || 0;
    const taxEncouragedThreshold = +merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold || 0;
    const hasAtLeastOneTax = selectedAppliedLineItemLevelTaxes.length > 0;
    const hasTaxSatisfied = hasAtLeastOneTax || doesServiceChargeNameContainTax(selectedInvoice.selectedServiceCharge);
    if (taxMandatoryThreshold > 0 && selectedInvoice.finalAmount >= taxMandatoryThreshold && !hasTaxSatisfied) {
      isPageIncomplete = true;
      errors = { ...errors, taxRequiredAboveMandatory: true };
    }
    const isAboveEncouragedBelowMandatoryNoTax =
      taxEncouragedThreshold > 0 &&
      selectedInvoice.finalAmount >= taxEncouragedThreshold &&
      (taxMandatoryThreshold === 0 || selectedInvoice.finalAmount < taxMandatoryThreshold) &&
      !hasTaxSatisfied;
    if (isAboveEncouragedBelowMandatoryNoTax && isPageIncomplete) {
      errors = { ...errors, taxEncouragedWarning: true };
    }

    if (isPageIncomplete) {
      lastShownValidationErrorsRef.current = errors;
      setShowInvoiceErrorsPopup({ open: true, errors });
      const validationFailedKeys = Object.entries(errors)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (errors.taxRequiredAboveMandatory) {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_tax_mandatory,
          ValidationFailed: validationFailedKeys.join(',')
        });
      } else {
        logAnalyticEvent(webInvoiceCreateRiskControl, {
          ActionType: RiskControlActionType.frontend_validation_failed,
          ValidationFailed: validationFailedKeys.join(',')
        });
      }
      return;
    }

    if (isAboveEncouragedBelowMandatoryNoTax) {
      setShowTaxEncouragedPopup(true);
      logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_shown });
      return;
    }

    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }

    const invoiceTotal = +selectedInvoice.finalAmount;

    // Check if partial payment is enabled but invoice total exceeds invoice limit
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }

    // Check if partial payment is enabled and invoice total exceeds any payment method limit
    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }

    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleCreateInvoiceV8();
    }
  };

  const handleProceedFromTaxEncouraged = () => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_continued });
    const isSelfCheckoutEmpty =
      selectedInvoice.invoicePaymentModes.filter(item => item.isSelected && item.teamLevelVisiblity).length === 0;
    if (isSelfCheckoutEmpty) {
      setShowNoSelfCheckoutPopup(true);
      return;
    }
    const invoiceTotal = +selectedInvoice.finalAmount;
    if (partialPaymentEnabled && invoiceTotal > 0 && invoiceTotal > +invoiceLimits.invoiceMaxLimit) {
      setShowInvoiceLimitExceededPopup(true);
      return;
    }
    if (partialPaymentEnabled && invoiceTotal > 0) {
      const exceedsLimit = checkIfInvoiceTotalExceedsPaymentModeLimit(invoiceTotal, invoicePaymentModes);
      if (exceedsLimit) {
        setShowPartialPaymentAvailablePopup(true);
        return;
      }
    }
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleCreateInvoiceV8();
    }
  };

  const createAndShareHandler = useCallback(throttle(onCreateAndShare, 800), [selectedInvoice, isInvoiceFromJob]);

  useEffect(() => {
    if (getPreviewSuccess) {
      const pdf = new Blob([getInvoicePDFPreviewResponse], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdf);
      setPreviewUrl(url);
    }
  }, [getPreviewSuccess]);

  const renderDate = (dateString: string | null) => {
    if (!dateString) {
      const today = DateUtils.getToday();
      return DateUtils.getDateInFormat({ date: new Date(today), dateFormat: 'dd MMM yyyy' });
    }
    const date = DateUtils.parseISODateString(dateString);
    return DateUtils.getDateInFormat({ date, dateFormat: 'dd MMM yyyy' });
  };

  const handleOnCreateInvoiceWithoutSelfCheckout = () => {
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleCreateInvoiceV8();
    }
  };

  const handleOnEditPaymentMethods = () => {
    handleCloseNoSelfCheckoutPopup();
    goBack();
  };

  const handleCloseNoSelfCheckoutPopup = () => {
    setShowNoSelfCheckoutPopup(false);
  };

  const handleCloseInvoiceErrorsPopup = () => {
    const lastErrors = lastShownValidationErrorsRef.current;
    setShowInvoiceErrorsPopup({ open: false, errors: InvoiceErrorFieldsDefault });
    if (lastErrors) {
      logAnalyticEvent(webInvoiceCreateRiskControl, {
        ActionType: RiskControlActionType.frontend_validation_dialog_closed,
        ValidationFailed: Object.entries(lastErrors)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(',')
      });
      lastShownValidationErrorsRef.current = null;
    }
  };

  const handleCloseTaxEncouragedPopup = () => {
    setShowTaxEncouragedPopup(false);
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_closed });
  };

  const handleAddTaxFromTaxEncouraged = () => {
    setShowTaxEncouragedPopup(false);
    goBack();
    logAnalyticEvent(webInvoiceCreateRiskControl, { ActionType: RiskControlActionType.tax_encouraged_add_tax });
  };

  const handleClosePartialPaymentAvailablePopup = () => {
    setShowPartialPaymentAvailablePopup(false);
  };

  const handleCreateAnyway = () => {
    handleClosePartialPaymentAvailablePopup();
    if (
      isInvoiceFromJob &&
      hasChangesToSync(
        selectedInvoice.invoiceCustomer,
        selectedInvoice.initialInvoiceCustomer,
        selectedInvoice.invoiceLineItems,
        selectedInvoice.initialInvoiceLineItems,
        selectedInvoice.selectedServiceCharge,
        selectedInvoice.initialServiceCharge,
        selectedInvoice.invoiceSubTotal,
        selectedInvoice.initialInvoiceSubTotal,
        selectedInvoice.isServiceFeeEnabled,
        selectedInvoice.isServiceFeeEnabledInitial,
        selectedInvoice.currentSurcharge,
        selectedInvoice.initialSurcharge
      )
    ) {
      setShowSyncConfirmationPopup(true);
    } else {
      handleCreateInvoiceV8();
    }
  };

  const handleEditInvoiceFromPartialPaymentPopup = () => {
    handleClosePartialPaymentAvailablePopup();
    goBack();
  };

  const handleCloseInvoiceLimitExceededPopup = () => {
    setShowInvoiceLimitExceededPopup(false);
  };

  const handleEditInvoiceFromLimitExceededPopup = () => {
    handleCloseInvoiceLimitExceededPopup();
    goBack();
  };

  const onPDFPreviewClick = () => {
    if (!previewUrl) {
      const invoicePayloadPreview = getPDFPreviewPayload(selectedInvoice, currentTeam, taxesAndFees.tnc, invoiceNumber);
      getInvoicePDFPreview({ teamId: currentTeam.id.toString(), payload: invoicePayloadPreview });
    }
    setPageState(PageState.PDF_VIEW);
  };

  return (
    <CreateInvoiceContext.Provider value={{ selectedInvoiceTab, setSelectedInvoiceTab }}>
      <div className="flex h-full w-full flex-col border">
        <InvoiceHeader
          title="Create & Share"
          pageState={pageState}
          setPageState={setPageState}
          onPDFPreviewClick={onPDFPreviewClick}
          editAndShareHandler={createAndShareHandler}
          goBack={goBack}
          isActionLoading={isCreateInvoiceLoading}
        />

        {pageState === PageState.BUYER_VIEW && (
          <div className="flex grow flex-col gap-4 bg-borderGray px-[149px] pt-8 pb-[102px]">
            <div className="flex gap-8">
              <div className="flex w-[675px] flex-col gap-[30px]">
                <BillToSection customer={selectedCustomerForInvoice} />

                <InvoiceLineItems
                  items={selectedLineItemsForInvoice}
                  billAmount={selectInvoiceSubtotalForInvoice}
                  surChargeAmount={+selectedInvoice.currentSurcharge?.valueInCurrencyWithBps || 0}
                  serviceFeeEnabled={isServiceFeeEnabled}
                  serviceCharges={selectedServiceCharge}
                  taxes={selectedAppliedLineItemLevelTaxes}
                  discountData={selectDiscountFormDataForInvoice}
                  totalAmount={selectInvoiceFinalAmountForInvoice}
                  selectDiscountSettingsForInvoice={selectDiscountSettingsForInvoice}
                />

                {selectInvoiceNotesForInvoice && <NotesSection notes={selectInvoiceNotesForInvoice} />}

                {taxesAndFees.tnc.enabled && <TermsSection terms={taxesAndFees.tnc.tnc} />}

                {selectInvoiceAttachmentsForInvoice && selectInvoiceAttachmentsForInvoice.length > 0 && (
                  <AttachmentsSection attachments={selectInvoiceAttachmentsForInvoice} />
                )}
              </div>
              <div className="flex w-[435px] flex-col gap-[30px]">
                <InvoiceTotalSection
                  totalAmount={selectInvoiceFinalAmountForInvoice.toString()}
                  dueDate={renderDate(dueDate)}
                  invoiceDate={renderDate(invoiceDate)}
                  status={InvoiceStatus.PENDING}
                  statusIndicator={StatusIndicator.NO_PAYMENTS_YET}
                />
                <TeamDetailsSection
                  teamName={currentTeam.name}
                  teamPhone={getTeamProfileData.phone}
                  teamEmail={getTeamProfileData.email}
                  teamBillingAddress={getTeamProfileData.address}
                  teamWebsite={getTeamProfileData.website}
                />
              </div>
            </div>
          </div>
        )}

        {pageState === PageState.PDF_VIEW && (
          <>
            {previewUrl && (
              <div>
                <object
                  style={{
                    height: window.innerHeight,
                    width: window.innerWidth
                  }}
                  data={`${previewUrl}#toolbar=0`}
                  type="application/pdf"></object>
              </div>
            )}
            {isPDFPreviewFetching && <Loader />}
            {isPDFPreviewError && <GenericError handleErrorBack={() => setPageState(PageState.BUYER_VIEW)} />}
          </>
        )}
      </div>
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title="Save and sync changes to the related job?"
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={`Changes in your invoices will be applied to the associated job ${invoiceFromJobNumber}.`}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateInvoiceFromJob}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => <CrossIconvector height={18} width={18} title="sync-popup-close" />}
          isActionInProgress={isCreateInvoiceLoading}
          height="h-[262px]"
          className="!px-5 !py-6"
        />
      )}
      {showNoSelfCheckoutPopup && (
        <NoSelfCheckoutPopup
          isEdit={false}
          onContinue={handleOnCreateInvoiceWithoutSelfCheckout}
          onEditPaymentMethods={handleOnEditPaymentMethods}
          onClose={handleCloseNoSelfCheckoutPopup}
          isActionLoading={isCreateInvoiceLoading}
        />
      )}
      {showInvoiceErrorsPopup.open && (
        <InvoiceErrorsPopup
          onClose={handleCloseInvoiceErrorsPopup}
          errorFields={showInvoiceErrorsPopup.errors}
          maximumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMaxLimit : +merchantCustomisations?.maximumAmount || 0
          }
          minimumAmount={
            partialPaymentEnabled ? +invoiceLimits.invoiceMinLimit : +merchantCustomisations?.minimumAmount || 0
          }
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          taxMandatoryThreshold={merchantCustomisations?.invoiceLimits?.taxMandatoryThreshold}
          onContinue={handleCloseInvoiceErrorsPopup}
        />
      )}
      {showTaxEncouragedPopup && (
        <InvoiceTaxEncouragedPopup
          onClose={handleCloseTaxEncouragedPopup}
          onAddTax={handleAddTaxFromTaxEncouraged}
          onProceedAnyway={handleProceedFromTaxEncouraged}
          isEdit={false}
          taxEncouragedThreshold={merchantCustomisations?.invoiceLimits?.taxEncouragedThreshold}
          isLoading={isCreateInvoiceLoading}
        />
      )}
      {showPartialPaymentAvailablePopup && (
        <PartialPaymentAvailablePopup
          onClose={handleClosePartialPaymentAvailablePopup}
          paymentModes={invoicePaymentModes}
          onCreateAnyway={handleCreateAnyway}
          onEditInvoice={handleEditInvoiceFromPartialPaymentPopup}
        />
      )}
      {showInvoiceLimitExceededPopup && (
        <InvoiceLimitExceededPopup
          onClose={handleCloseInvoiceLimitExceededPopup}
          invoiceLimits={invoiceLimits}
          onEditInvoice={handleEditInvoiceFromLimitExceededPopup}
        />
      )}
      <InvoiceRiskWarningsPopup
        open={showRiskWarningsPopup}
        warnings={riskWarnings}
        onCreateAnyway={handleRiskWarningsCreateAnyway}
        onGotIt={handleRiskWarningsGotIt}
        isEdit={false}
        isLoading={isCreateInvoiceLoading}
      />
    </CreateInvoiceContext.Provider>
  );
};

export default BuyersPreview;
